import { useState } from "react";
import { Phone, Mail, MapPin, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/Primitives";
import { api, formatApiError, trackEvent } from "@/lib/api";
import { BUSINESS, STAFF } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const empty = { name: "", email: "", phone: "", subject: "General Inquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/\S+@\S+\.\S+/.test(form.email) || !form.message) {
      toast.error("Please complete name, a valid email and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      trackEvent("form_complete", { form: "contact" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Contact" description="Contact Mom's Best Friend Sitters in Dallas. Call 972-476-0500 or send us a message — we're happy to help you plan trusted care." breadcrumbs={[{ name: "Home", item: "/" }, { name: "Contact", item: "/contact" }]} />
      <PageHero overline="Get in touch" title="We'd love to hear from you" subtitle="Whether you're planning care or just have a question, our team is a phone call or message away." />

      <section className="container-mbf py-16 md:py-24 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="space-y-5">
            <a href={BUSINESS.phoneHref} onClick={() => trackEvent("call_click", { source: "contact" })} className="card-warm p-6 flex items-center gap-4 group" data-testid="contact-phone-card">
              <span className="w-12 h-12 rounded-full bg-tinted text-terracotta flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></span>
              <div><div className="text-xs uppercase tracking-wider text-stoneish">Call us</div><div className="font-semibold text-espresso group-hover:text-terracotta transition-colors">{BUSINESS.phone}</div></div>
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="card-warm p-6 flex items-center gap-4 group" data-testid="contact-email-card">
              <span className="w-12 h-12 rounded-full bg-tinted text-terracotta flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
              <div><div className="text-xs uppercase tracking-wider text-stoneish">Email us</div><div className="font-semibold text-espresso group-hover:text-terracotta transition-colors break-all">{BUSINESS.email}</div></div>
            </a>
            <div className="card-warm p-6 flex items-center gap-4" data-testid="contact-address-card">
              <span className="w-12 h-12 rounded-full bg-tinted text-terracotta flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></span>
              <div><div className="text-xs uppercase tracking-wider text-stoneish">Visit us</div><div className="font-semibold text-espresso">{BUSINESS.address}</div></div>
            </div>
            <div className="card-warm p-6" data-testid="contact-staff-card">
              <div className="text-xs uppercase tracking-wider text-stoneish mb-3">Ask for the right person</div>
              <ul className="space-y-2.5 text-sm">
                {STAFF.map((s) => (
                  <li key={s.name} className="flex items-center justify-between border-b border-[#F1E6D0] last:border-0 pb-2.5 last:pb-0">
                    <span className="font-semibold text-espresso">{s.name}</span><span className="text-stoneish">{s.role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div>
          {done ? (
            <div className="card-warm p-10 text-center" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8" /></div>
              <h2 className="font-serif text-2xl text-espresso mb-2">Message sent!</h2>
              <p className="text-sm text-stoneish">Thank you for reaching out. We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="card-warm p-7 md:p-9 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label htmlFor="name" className="mb-2 block text-espresso">Name *</Label><Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" /></div>
                <div><Label htmlFor="phone" className="mb-2 block text-espresso">Phone</Label><Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" /></div>
              </div>
              <div><Label htmlFor="email" className="mb-2 block text-espresso">Email *</Label><Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" /></div>
              <div><Label htmlFor="subject" className="mb-2 block text-espresso">Subject</Label><Input id="subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject-input" /></div>
              <div><Label htmlFor="message" className="mb-2 block text-espresso">Message *</Label><Textarea id="message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message-input" /></div>
              <button type="submit" disabled={submitting} className="btn-accent w-full !py-4 disabled:opacity-60" data-testid="contact-submit-btn">
                {submitting ? "Sending…" : "Send message"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
